
import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { BaseContentCardProps } from "@/components/dashboard/content-card/types";

/**
 * Unified Content Card component that provides consistent styling across the app
 * - Includes a colored line at the bottom (using color prop)
 * - Consistent hover effects and cursor behavior
 * - Full height support for grid layouts
 */
const ContentCard = forwardRef<HTMLDivElement, BaseContentCardProps>(({
  color,
  defaultColor = "#6B7789",
  isClickable = true,
  onClick,
  fullHeight = false,
  className,
  withPadding = false,
  children,
  ...props
}, ref) => {
  // Only handle clicks if this is NOT being used as a wrapper component
  // When used as a wrapper (like in ContentCard), the children handle their own clicks
  const isWrapper = React.Children.count(children) > 0 && 
    React.Children.toArray(children).some(child => 
      React.isValidElement(child) && typeof child.type !== 'string'
    );

  const handleClick = () => {
    // Only handle clicks if we're not a wrapper and have a click handler
    if (!isWrapper && isClickable && onClick) {
      onClick();
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col rounded-lg overflow-hidden shadow-sm border border-border bg-card",
        !isWrapper && isClickable && onClick && "hover:shadow-md transition-shadow cursor-pointer",
        fullHeight && "h-full",
        withPadding && "p-4",
        className
      )}
      onClick={!isWrapper && isClickable ? handleClick : undefined}
      {...props}
    >
      <div className="flex-1">
        {children}
      </div>
      
      {/* Colored line at bottom - consistent across all card instances */}
      <div 
        className="h-[4px] w-full" 
        style={{ backgroundColor: color || defaultColor }}
      />
    </div>
  );
});

ContentCard.displayName = "ContentCard";

export { ContentCard };
